import services from 'services/services';
import { useMutation, useQuery, useQueryClient } from 'react-query';

// Import helpers
import {
	changeAgreementsConfirmedStatus,
	filterAgreementsByType,
	filterConfirmedAgreementsByID,
	filterConfirmedAgreementsByIdAndType,
	filterConfirmedAgreementsByType,
	getAgreementsByType,
	getUniqueAgreements,
	removeInitialAcceptedAgreements
} from './helpers';
import {
	ACCEPT_SUBSCRIBER_AGREEMENTS_API,
	DECLINE_SUBSCRIBER_AGREEMENTS_API,
	GET_SUBSCRIBER_AGREEMENTS_API
} from 'helpers/api';

const handleAcceptSubscriberAgreements = (agreement_version_ids) =>
	services.post(ACCEPT_SUBSCRIBER_AGREEMENTS_API, {
		agreement_version_ids
	});

const successAcceptSubscriberAgreements = (queryKey, queryClient) => (
	_,
	agreementVersionIDs
) => {
	queryClient.setQueryData(queryKey, (prevAgreements) =>
		changeAgreementsConfirmedStatus({
			prevAgreements,
			agreementVersionIDs,
			confirmed: true
		})
	);
};

const handleDeclineSubscriberAgreements = (agreement_version_ids) =>
	services.post(DECLINE_SUBSCRIBER_AGREEMENTS_API, {
		agreement_version_ids
	});

const successDeclineSubscriberAgreements = (queryKey, queryClient) => (
	_,
	agreementVersionIDs
) => {
	queryClient.setQueryData(queryKey, (prevAgreements) =>
		changeAgreementsConfirmedStatus({
			prevAgreements,
			agreementVersionIDs,
			confirmed: false
		})
	);
};

const useFetchAgreements = ({
	queryKey,
	agreementsToFetch = [],
	useCache = false,
	fetchSubscriberAgreements = false,
	subscriberAgreementsToFetch = [],
	removeInitialAccepted = false,
	initialAcceptedToRemove = [],
	isEnabled = true
}) => {
	const queryClient = useQueryClient();

	const fetchAgreements = async () => {
		if (fetchSubscriberAgreements || subscriberAgreementsToFetch.length) {
			const { data: subscriberAgreements } = await services.get(
				GET_SUBSCRIBER_AGREEMENTS_API
			);

			if (agreementsToFetch.length) {
				const agreements = await getAgreementsByType(agreementsToFetch);

				if (subscriberAgreementsToFetch.length) {
					const filteredSubscriberAgreements = filterAgreementsByType(
						subscriberAgreements,
						subscriberAgreementsToFetch
					);

					return getUniqueAgreements([
						...filteredSubscriberAgreements,
						...agreements
					]);
				}

				return getUniqueAgreements([...subscriberAgreements, ...agreements]);
			}

			if (subscriberAgreementsToFetch.length) {
				return filterAgreementsByType(
					subscriberAgreements,
					subscriberAgreementsToFetch
				);
			}

			return subscriberAgreements;
		}

		return await getAgreementsByType(agreementsToFetch);
	};

	const filterAgreements = async () => {
		const agreementsCache = queryClient.getQueryData(queryKey);

		if (agreementsCache?.length && useCache) {
			return agreementsCache;
		}

		const agreements = await fetchAgreements();

		if (removeInitialAccepted || initialAcceptedToRemove.length) {
			if (!fetchSubscriberAgreements && !subscriberAgreementsToFetch.length) {
				const { data: subscriberAgreements } = await services.get(
					GET_SUBSCRIBER_AGREEMENTS_API
				);

				if (initialAcceptedToRemove.length) {
					return filterConfirmedAgreementsByIdAndType(
						agreements,
						subscriberAgreements,
						initialAcceptedToRemove
					);
				}

				return filterConfirmedAgreementsByID(agreements, subscriberAgreements);
			}

			if (initialAcceptedToRemove.length) {
				return filterConfirmedAgreementsByType(
					agreements,
					initialAcceptedToRemove
				);
			}

			return removeInitialAcceptedAgreements(agreements);
		}

		return agreements;
	};

	const {
		isError: isAgreementsError,
		isLoading: isAgreementsLoading,
		isFetching: isAgreementsFetching,
		data: agreements
	} = useQuery({
		queryKey,
		queryFn: filterAgreements,
		initialData: [],
		enabled: isEnabled
	});

	const acceptSubscriberAgreements = useMutation({
		mutationFn: handleAcceptSubscriberAgreements,
		onSuccess: successAcceptSubscriberAgreements(queryKey, queryClient)
	});

	const declineSubscriberAgreements = useMutation({
		mutationFn: handleDeclineSubscriberAgreements,
		onSuccess: successDeclineSubscriberAgreements(queryKey, queryClient)
	});

	return {
		agreements,
		isAgreementsError,
		isAgreementsLoading,
		isAgreementsFetching,
		acceptSubscriberAgreements,
		declineSubscriberAgreements
	};
};

export default useFetchAgreements;
